/*
 * Levels Beyond CONFIDENTIAL
 *
 * Copyright 2003 - 2020 Levels Beyond Incorporated
 * All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains the
 * property of Levels Beyond Incorporated and its suppliers, if
 * any. The intellectual and technical concepts contained herein
 * are proprietary to Levels Beyond Incorporated and its suppliers
 * and may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this
 * material is unlawful and strictly forbidden unless prior
 * written permission is obtained from Levels Beyond Incorporated.
 */

/**
 * Created by seaster on 1/4/16.
 */

function ContentEditServiceConstructor(
    this: JS5Object,
    _: JS5Object,
    $location: JS5Object,
    $q: JS5Object,
    $route: JS5Object,
    $routeParams: JS5Object,
    AcceptProfileResourceFactory: JS5Object,
    BreadcrumbService: JS5Object,
    CategoriesResourceFactory: JS5Object,
    ContentResourceFactory: JS5Object,
    EnumService: JS5Object,
    GenresResourceFactory: JS5Object,
    LanguageResourceFactory: JS5Object,
    LivefeedsResourceFactory: JS5Object,
    MetadataEditService: JS5Object,
    MetadataService: JS5Object,
    ModalService: JS5Object,
    NetworkResourceFactory: JS5Object,
    NotificationService: JS5Object,
    PackageAcceptTemplatesResourceFactory: JS5Object,
    PackageResourceFactory: JS5Object,
    PermissionsService: JS5Object,
    RoleResourceFactory: JS5Object,
    UtilsService: JS5Object,
) {
    let originalContentData = {};
    let originalContentCategoryData = {};

    let setBreakPrePostrollPermissions = (permissions: JS5Object) => {
        let readPerms = _.get(permissions, 'preRoll.read') && _.get(permissions, 'postRoll.read');
        let writePerms = _.get(permissions, 'preRoll.write') && _.get(permissions, 'postRoll.write');

        permissions.breakprepostroll = {read: readPerms, write: writePerms};
        return permissions;
    };

    let getTabViewPermissions = (permissionViewStates: JS5Object[]) => {
        let promises: JS5Object = {};
        _.forEach(permissionViewStates, (value: JS5Object, key: string) => {
            promises[key] = PermissionsService.getReadWritePermissions(value);
        });

        return $q.all(promises).then((permissions: JS5Object) => {
            return setBreakPrePostrollPermissions(permissions);
        });
    };

    this.contentObject = null;
    this.metadataComponentsMap = null;
    this.editorForm = null;
    this.originalPackageAcceptTemplateData = null;
    this.genres = null;
    this.networks = null;
    this.contentCategoryObject = null;
    this.allCategories = null;
    this.contentCategoryRoles = null;
    this.languages = null;
    this.ccRoles = null;
    this.subtitleRoles = null;
    this.videoAcceptProfiles = null;

    this.tabRevertFunction = null;
    this.tabSaveSuccessFunction = null;
    this.tabSaveErrorFunction = null;
    this.originalContentCategoryObject = null;
    this.contentBreadcrumb = {
        location: '',
        name: '',
        params: null,
    };

    this.categoryBreadcrumb = {
        location: '',
        name: '',
        params: null,
    };

    this.resetBreadcrumbs = () => {
        const contentPathKeyword = UtilsService.getPathKeywordFromContentType(this.contentType);
        BreadcrumbService.clearBreadcrumbs();
        if (!_.isNil(contentPathKeyword)) {
            BreadcrumbService.addBreadcrumb({
                name: this.contentType,
                location: `/meta/${contentPathKeyword}/search`,
                params: angular.copy($location.search())
            });
        }
    };

    let closeEditView = () => {
        BreadcrumbService.clearBreadcrumbs();
        this.contentObject = null;
        this.editorForm = null;
        $location.path(`/meta/${UtilsService.getPathKeywordFromContentType(this.contentType)}/search`);
    };

    let closeContentCategoryEditView = () => {
        this.resetBreadcrumbs();
        this.contentCategoryObject = originalContentCategoryData;
        this.editorForm = null;
        $location.path(`/meta/${UtilsService.getPathKeywordFromContentType(this.contentType)}/edit/${this.contentObject.id}/categories`);
    };

    this.getViewPermissions = (viewStates: JS5Object[]) => {
        return getTabViewPermissions(viewStates);
    };

    // Assumes EnumService.queryForEnums() has been called & promise resolved
    this.setAvailableBreakPatterns = () => {
        this.availableBreakPatterns = EnumService.getEnumPicklist('AbstractShow_breakPrePostRollPattern');
    };

    this.getEditData = () => {
        let contentReady = $q.defer();
        if ($routeParams.id > -1) {
            this.contentObject = ContentResourceFactory.get({id: $routeParams.id});
            this.contentObject.$promise.then((contentData: JS5Object) => {
                originalContentData = angular.copy(contentData);
                contentReady.resolve(contentData);
            });
        } else {
            this.contentObject = new ContentResourceFactory({type: this.contentType, airTime: '00:00'});
            contentReady.resolve(this.contentObject);
        }
        this.genres = GenresResourceFactory.query({fetchLimit: 0});
        this.networks = NetworkResourceFactory.getNetworks({fetchLimit: 0});

        let editDataDeferred = $q.defer();
        $q.all({
            content: contentReady.promise,
            genres: this.genres.$promise,
            networks: this.networks.$promise
        }).then((contentData: JS5Object) => {
            $q.all({
                metadataComponents: this.getContentComponentsMap(),
                liveFeeds: this.getAvailableLiveFeeds().$promise,
            }).then((auxData: JS5Object) => {
                editDataDeferred.resolve({
                    content: contentData.content,
                    genres: contentData.genres,
                    networks: contentData.networks,
                    metadataComponents: auxData.metadataComponents,
                    liveFeeds: auxData.liveFeeds,
                });
            });
        });
        return editDataDeferred.promise;
    };

    this.getContentPackageAcceptTemplatesEditData = (contentId: string) => {
        let contentPromises = {
            packageAcceptTemplates: PackageAcceptTemplatesResourceFactory.query({view: 'EXTENDED'}).$promise,
            contentPackageAcceptTemplateContainers: ContentResourceFactory.getPackageAcceptTemplates({id: contentId}).$promise,
            validationTypes: EnumService.queryForEnums().then(() => EnumService.getEnumPicklist('ContentPackageAcceptTemplateMember_validationType')),
            categoryTypes: CategoriesResourceFactory.getCategoryTypes().$promise
        };

        return $q.all(contentPromises).then((data: JS5Object) => {
            _.forEach(data.packageAcceptTemplates, (template: JS5Object) => {
                template.type = _.camelCase(template.name);
                template.mediaAcceptProfiles = _.groupBy(template.mediaAcceptProfiles, 'type');
            });

            _.forEach(data.contentPackageAcceptTemplateContainers, (container: JS5Object) => {
                container.members = _.groupBy(container.members, 'acceptProfile.type');
            });

            _.forEach(data.categoryTypes, (categoryType: string) => {
                if (!_.find(_.get(data, 'contentPackageAcceptTemplateContainers'), {categoryType: categoryType})) {
                    data.contentPackageAcceptTemplateContainers.push({
                        categoryType: categoryType,
                        members: [],
                        packageAcceptTemplate: {}
                    });
                }
            });
            data.contentPackageAcceptTemplateContainers = _
                .orderBy(data.contentPackageAcceptTemplateContainers, 'categoryType.name', 'asc');

            return data;
        });
    };

    this.updateContentPackageAcceptTemplates = (contentId: string, cPats: JS5Object[]) => {
        return ContentResourceFactory.updatePackageAcceptTemplates({id: contentId}, cPats)
            .$promise.then((data: JS5Object[]) => {
                data = _.orderBy(data, 'categoryType.name', 'asc');
                _.forEach(data, (container: JS5Object) => {
                    container.members = _.groupBy(container.members, 'acceptProfile.type');
                });
                return data;
            });
    };

    this.getContentCategoryEditData = () => {
        let contentReady = $q.defer();
        this.contentObject = ContentResourceFactory.get({id: $routeParams.id});
        if ($routeParams.contentCategoryId > -1) {
            this.contentCategoryObject = ContentResourceFactory.getContentCategory({contentCategoryId: $routeParams.contentCategoryId});
            this.contentCategoryObject.$promise.then((contentCategory: JS5Object) => {
                originalContentCategoryData = _.cloneDeep(contentCategory);
                contentReady.resolve(contentCategory);
            });
        } else {
            contentReady.resolve(this.contentCategoryObject);
        }

        if (!this.allCategories) {
            this.allCategories = PackageResourceFactory.getCategories({view: 'EXTENDED'});
        }
        if (!this.contentCategoryRoles) {
            this.contentCategoryRoles = RoleResourceFactory.query({mediaType: 'Video'});
        }
        if (!this.languages) {
            this.languages = LanguageResourceFactory.query();
        }
        if (!this.ccRoles) {
            this.ccRoles = RoleResourceFactory.query({mediaType: 'CC'});
        }
        if (!this.subtitleRoles) {
            this.subtitleRoles = RoleResourceFactory.query({mediaType: 'Subtitle'});
        }
        if (!this.videoAcceptProfiles) {
            this.videoAcceptProfiles = AcceptProfileResourceFactory.query({profileType: 'VIDEO'});
        }
        return $q.all({
            content: this.contentObject.$promise,
            contentCategoryObject: contentReady.promise,
            allCategories: this.allCategories.$promise,
            contentCategoryRoles: this.contentCategoryRoles.$promise,
            languages: this.languages.$promise,
            ccRoles: this.ccRoles.$promise,
            subtitleRoles: this.subtitleRoles.$promise,
            videoAcceptProfiles: this.videoAcceptProfiles.$promise
        });
    };

    this.getAvailableLiveFeeds = () => {
        this.liveFeeds = LivefeedsResourceFactory.query();
        return this.liveFeeds;
    };

    this.showNewContentCategoryModal = () => {
        ModalService.showAddContentCategoryModal($routeParams.id, this.handleAddContentCategoryModal);
    };

    this.openContentCategoryEditView = (contentCategoryObject: JS5Object) => {
        $location.url(
            `/meta/${UtilsService.getPathKeywordFromContentType(this.contentType)}` +
            `/edit/${this.contentObject.id}/categories/edit/${contentCategoryObject.id}`
        );
    };

    this.handleAddContentCategoryModal = (contentCategoryObject: JS5Object) => {
        this.contentCategoryObject = contentCategoryObject;
        this.openContentCategoryEditView(contentCategoryObject);
    };

    this.groupMetadataProperties = [];

    let getMetadataPropertiesByGroup = (groupName: string) => {
        let metadataGroupPromise;

        if (!this.groupMetadataProperties[groupName]) {
            metadataGroupPromise = MetadataEditService.getMetadataByGroup(groupName)
                .then((groupProperties: JS5Object[]) => {
                    this.groupMetadataProperties[groupName] = groupProperties;
                });
        } else {
            let deferred = $q.defer();
            deferred.resolve();
            metadataGroupPromise = deferred.promise;
        }

        return metadataGroupPromise;
    };

    let getContentMetadataDefs = () => {
        return getMetadataPropertiesByGroup(this.contentType);
    };

    this.getContentComponentsMap = () => {
        return getContentMetadataDefs().then(() => {
            let metadataItems: JS5Object = {};
            if (this.contentObject.metadata && this.contentObject.metadata.properties) {
                metadataItems = this.contentObject.metadata.properties;
            } else {
                _.forEach(this.groupMetadataProperties[this.contentType], (property: JS5Object) => {
                    metadataItems[property.name] = {
                        required: false,
                        valueOverridden: false
                    };
                });

                this.contentObject.metadata = {
                    properties: metadataItems
                };
            }
            this.contentMetadataComponentsMap = MetadataService.buildCustomComponentMap(metadataItems);

            return this.contentMetadataComponentsMap;
        });
    };

    this.revertEditData = () => {
        if (this.contentObject.id) {
            this.contentObject = angular.copy(originalContentData);
            this.metadataComponentsMap = MetadataService.buildCustomComponentMap(this.contentObject.metadata.properties);
            this.editorForm.$setPristine();
            if (this.tabRevertFunction) {
                this.tabRevertFunction();
            }
        } else {
            closeEditView();
        }
    };

    this.revertContentCategoryEditData = () => {
        if (this.contentCategoryObject.id > -1) {
            this.contentCategoryObject = angular.copy(originalContentCategoryData);
            this.editorForm.$setPristine();
        } else {
            closeContentCategoryEditView();
        }
    };

    this.saveEditData = () => {
        let savePromise = (this.contentObject.id > -1) ? this.contentObject.$update() : this.contentObject.$save();

        savePromise.then((contentData: JS5Object) => {
            originalContentData = _.cloneDeep(contentData);
            this.metadataComponentsMap = MetadataService.buildCustomComponentMap(this.contentObject.metadata.properties);
            this.editorForm.$setPristine();
            $route.updateParams({id: contentData.id});
            if (this.tabSaveSuccessFunction) {
                this.tabSaveSuccessFunction();
            }
        }, () => {
            if (this.tabSaveErrorFunction) {
                this.tabSaveErrorFunction();
            }
        });
        return savePromise;
    };

    this.contentCategorySaveValid = () => {
        let saveValid = true;
        if (this.contentCategoryObject.videoRoles) {
            angular.forEach(this.contentCategoryObject.videoRoles, (videoRole) => {
                if (videoRole.closedCaptioningAcceptTemplate) {
                    if (videoRole.closedCaptioningAcceptTemplate.languageRoleMap.length === 0) {
                        this.errorRole = videoRole.role.name;
                        saveValid = false;
                    }
                }
                if (videoRole.subtitleAcceptTemplate) {
                    if (videoRole.subtitleAcceptTemplate.languageRoleMap.length === 0) {
                        this.errorRole = videoRole.role.name;
                        saveValid = false;
                    }
                }
            });
        }
        return saveValid;
    };

    this.saveContentCategoryEditData = () => {
        if (this.contentCategorySaveValid()) {
            if ($routeParams.contentCategoryId > -1) {
                let updateContentCategoryPromise = ContentResourceFactory.updateContentCategory(
                    {
                        contentCategoryId: $routeParams.contentCategoryId
                    },
                    this.contentCategoryObject
                ).$promise;
                updateContentCategoryPromise.then((data: JS5Object) => {
                    originalContentCategoryData = _.cloneDeep(data);
                    this.contentCategoryObject = data;
                });

                return updateContentCategoryPromise;
            } else {
                this.contentCategoryObject.id = null;
                let saveCategoryPromise = ContentResourceFactory.saveContentCategory(
                    {
                        id: $routeParams.id
                    },
                    this.contentCategoryObject
                ).$promise;

                saveCategoryPromise.then((data: JS5Object) => {
                    originalContentCategoryData = angular.copy(data);
                    this.contentCategoryObject = data;
                    this.editorForm.$setPristine();
                    $route.updateParams({contentCategoryId: data.id});
                });

                return saveCategoryPromise;
            }
        } else {
            NotificationService.createWarningNotification(
                `You have no language roles selected for Role: ` +
                `${this.errorRole}. Please select at least one.`);
            return $q.reject('Save Invalid');
        }

    };

    this.showUnsavedChangesNotification = () => {
        NotificationService.createWarningNotification('You have unsaved changes. Please save or revert.', '');
    };

    this.exitEdit = () => {
        if (_.get(this, 'contentObject.id') !== -1 && this.editorForm.$pristine) {
            closeEditView();
        } else {
            this.showUnsavedChangesNotification();
        }
    };

    this.exitContentCategoryEdit = () => {
        if (this.contentCategoryObject.id && this.editorForm.$pristine) {
            closeContentCategoryEditView();
        } else {
            this.showUnsavedChangesNotification();
        }
    };

    if (BreadcrumbService.getBreadcrumbs().length === 0) {
        this.resetBreadcrumbs();
    }

    //
    // Seasons / Volumes Search View
    //

    this.getSeasons = (includeHidden: boolean) => {
        let query: JS5Object = {
            contentId: $routeParams.id,
        };
        if (includeHidden) {
            query.includeHidden = true;
        }

        return ContentResourceFactory.getSeasons(query).$promise;
    };

    this.getSeason = (seasonId: string, view: string=null, mvpdPartnerId: string=null) => {
        return ContentResourceFactory.getSeason({
            id: seasonId,
            view: view,
            mvpdPartnerId: mvpdPartnerId
        }).$promise;
    };

    this.saveSeason = (season: JS5Object) => {
        return ContentResourceFactory.updateSeason({seasonId: season.id}, season).$promise;
    };

    this.getVolumes = (includeHidden: boolean) => {
        let query: JS5Object = {
            contentId: $routeParams.id,
        };
        if (includeHidden) {
            query.includeHidden = true;
        }

        return ContentResourceFactory.getVolumes(query).$promise;
    };

    this.toggleSeasonVisibility = (season: JS5Object) => {
        return ContentResourceFactory.toggleSeasonVisibility({seasonId: season.id}).$promise;
    };

    this.toggleVolumeVisibility = (volume: JS5Object) => {
        return ContentResourceFactory.toggleVolumeVisibility({volumeId: volume.id}).$promise;
    };

    this.cloneSeason = (season: JS5Object) => {
        let seasonClone = _.cloneDeep(season);
        delete seasonClone.id;
        return ContentResourceFactory.createSeason({contentId: $routeParams.id}, seasonClone).$promise;
    };

    this.cloneVolume = (volume: JS5Object) => {
        let volumeClone = _.cloneDeep(volume);
        delete volumeClone.id;
        return ContentResourceFactory.createVolume({contentId: $routeParams.id}, volumeClone).$promise;
    };

    //
    // Seasons / Volumes Editor
    //

    this.seasonVolumeBreadcrumb = {
        name: '',
    };

    this.getContentObject = () => {
        return ContentResourceFactory.get({id: $routeParams.id}).$promise;
    };

    let getSeasonVolumeEditApi = (): JS5Object => {
        //
        // This returns an object containing all of the resource
        // functions required to manipulate season and volume
        // objects, as well as relevant object IDs from the
        // route parameters.
        //

        let idName = $routeParams.seasonId ? 'seasonId' : 'volumeId';
        let seasonVolumeId = $routeParams[idName];
        let contentId = $routeParams.id;

        let resourceMethodSuffix = $routeParams.seasonId ? 'Season' : 'Volume';

        return {
            id: seasonVolumeId,
            idName: idName,
            create: (seasonVolumeObject: JS5Object) => {
                return ContentResourceFactory['create' + resourceMethodSuffix](
                    {contentId: contentId},
                    seasonVolumeObject);
            },
            update: ContentResourceFactory['update' + resourceMethodSuffix],
            get: ContentResourceFactory['get' + resourceMethodSuffix],
        };
    };

    let setSeasonVolumeBreadcrumbs = () => {
        // content breadcrumb
        this.contentBreadcrumb.name = this.contentObject.name;
        this.contentBreadcrumb.location = '/meta/series/edit/' +
            $routeParams.id + '/' +
            ($routeParams.seasonId ? 'seasons' : 'volumes');

        // season/volume breadcrumb
        this.seasonVolumeBreadcrumb.name = this.seasonVolumeObject.name ?
            _.cloneDeep(this.seasonVolumeObject.name) :
            'New ' + ($routeParams.seasonId ? 'Season' : 'Volume');
    };

    this.setContentSeasonBreadcrumbs = () => {

        // content breadcrumb
        this.contentBreadcrumb.name = this.contentObject.name;
        this.contentBreadcrumb.location = '/meta/series/edit/' +
            $routeParams.id + '/' +
            ($routeParams.seasonId ? 'seasons' : 'volumes');

        // season/volume breadcrumb
        this.seasonVolumeBreadcrumb.name = this.seasonObject.name ? _.cloneDeep(this.seasonObject.name) : 'New ' + ($routeParams.seasonId ? 'Season' : 'Volume');

    };

    let getSeasonVolumeEditDataPromise = (seasonVolumeResourceMethods: JS5Object) => {
        let seasonVolumePromise;
        if (seasonVolumeResourceMethods.id < 0) {
            // ID is negative = create a new season/volume object
            let seasonVolumeDeferred = $q.defer();
            seasonVolumeDeferred.resolve({
                metadata: {
                    properties: {}
                }
            });
            seasonVolumePromise = seasonVolumeDeferred.promise;
        } else {
            // ID is non-negative = query for season volume object
            seasonVolumePromise = seasonVolumeResourceMethods.get({id: seasonVolumeResourceMethods.id}).$promise;
        }

        return seasonVolumePromise;
    };

    this.setBrandingBreadcrumbs = () => {
        if (this.contentObject) {
            return $q.when();
        }
        return $q.all({
            seasonVolumeObject: getSeasonVolumeEditDataPromise(getSeasonVolumeEditApi()),
            contentObject: this.getContentObject(),
        }).then((seasonVolumeData: JS5Object) => {
            this.seasonVolumeObject = seasonVolumeData.seasonVolumeObject;
            this.contentObject = seasonVolumeData.contentObject;

            setSeasonVolumeBreadcrumbs();
        });
    };

    this.getSeasonVolumeEditData = () => {
        let seasonVolumeResourceMethods = getSeasonVolumeEditApi();
        let deferred = $q.defer();

        // only fetch data if necessary, e.g. if ID in route
        // params doesn't match the data we already have
        let seasonVolumeEditDataReady = this.seasonVolumeObject && this.seasonVolumeObject.id === seasonVolumeResourceMethods.id;

        if (!seasonVolumeEditDataReady) {
            // We do not already have the data, so we'll fetch it
            // from the server (or create a new copy if need be)
            // now.

            $q.all({
                seasonVolumeObject: getSeasonVolumeEditDataPromise(seasonVolumeResourceMethods),
                contentObject: this.getContentObject(),
            }).then((seasonVolumeEditData: JS5Object) => {
                this.seasonVolumeObject = seasonVolumeEditData.seasonVolumeObject;
                this.contentObject = seasonVolumeEditData.contentObject;

                this.originalSeasonVolumeObject = _.cloneDeep(seasonVolumeEditData.seasonVolumeObject);

                setSeasonVolumeBreadcrumbs();
                deferred.resolve({
                    seasonVolumeObject: this.seasonVolumeObject,
                    contentObject: this.contentObject,
                });
            });
        } else {
            // We already have the latest data from the server, as
            // well as any updates made through saving, so
            // re-resolve the deferred with our local data.

            setSeasonVolumeBreadcrumbs();
            deferred.resolve({
                seasonVolumeObject: this.seasonVolumeObject,
                contentObject: this.contentObject,
            });
        }

        return deferred.promise;
    };

    this.saveSeasonVolume = () => {
        let promise: JS5Object;
        let seasonVolumeResourceMethods: JS5Object = getSeasonVolumeEditApi();

        if (seasonVolumeResourceMethods.id < 0) {
            promise = seasonVolumeResourceMethods.create(this.seasonVolumeObject).$promise;
        } else {
            let updateParams: JS5Object = {};
            updateParams[seasonVolumeResourceMethods.idName] = seasonVolumeResourceMethods.id;
            promise = seasonVolumeResourceMethods.update(updateParams, this.seasonVolumeObject).$promise;
        }

        promise.then((seasonVolume: JS5Object) => {
            this.seasonVolumeObject = seasonVolume;
            this.originalSeasonVolumeObject = _.cloneDeep(seasonVolume);

            let newRouteParams: JS5Object = {};
            newRouteParams[seasonVolumeResourceMethods.idName] = seasonVolume.id;
            $route.updateParams(newRouteParams);

            setSeasonVolumeBreadcrumbs();

            this.editorForm.$setPristine();
        });

        return promise;
    };

    this.closeSeasonVolumeEditor = () => {
        if (this.editorForm && this.editorForm.$dirty) {
            this.showUnsavedChangesNotification();
        } else {
            this.resetBreadcrumbs();
            this.seasonVolumeObject = null;
            this.originalSeasonVolumeObject = null;
            this.editorForm = null;
            $location.url('/meta/series/edit/' + $routeParams.id + '/' +
                ($routeParams.seasonId ? 'seasons' : 'volumes'));
        }
    };

    this.revertSeasonVolume = () => {
        this.editorForm.$setPristine();
        if (this.seasonVolumeObject.id !== undefined) {
            this.seasonVolumeObject = _.cloneDeep(this.originalSeasonVolumeObject);
        } else {
            this.closeSeasonVolumeEditor();
        }
    };

    let getSeasonVolumeMetadataDefs = () => {
        let groupName = $routeParams.seasonId ? 'Season' : 'Volume';
        return getMetadataPropertiesByGroup(groupName);
    };

    //
    // Note: don't call this unless this.seasonVolumeObject is up
    //		 to date - e.g. resolve getSeasonVolumeEditData() or
    //		 saveSeasonVolume() before calling this
    //
    this.getSeasonVolumeComponentsMap = () => {
        this.metadataComponentsMap = {};
        let deferred = $q.defer();

        return getSeasonVolumeMetadataDefs().then(() => {
            let metadataItems: JS5Object = {};

            let groupName = $routeParams.seasonId ? 'Season' : 'Volume';
            _.forEach(this.groupMetadataProperties[groupName], (property: JS5Object) => {
                // use property sent by DTO if it exists,
                // otherwise create a default from scratch
                if (this.seasonVolumeObject.metadata.properties.hasOwnProperty(property.name)) {
                    metadataItems[property.name] = this.seasonVolumeObject.metadata.properties[property.name];
                } else {
                    metadataItems[property.name] = {required: false, valueOverridden: false};
                }
            });

            this.metadataComponentsMap = MetadataService.buildCustomComponentMap(metadataItems);
            deferred.resolve(this.metadataComponentsMap);

            return deferred.promise;
        });
    };
}

angular
    .module('prism-ui.content')
    .service('ContentEditService', ContentEditServiceConstructor);
